import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2, ScrollText, ShieldCheck, TerminalSquare } from 'lucide-react';
import { ScrollStory } from './ScrollStory';
import { Reveal, Tilt, useIsDark } from './motion';
import { ShotVisual, GateVisual, BenchmarkVisual, SpecVisual, FrameworkVisual, McpVisual } from './FeatureVisuals';
import { SHOWCASE, ShowcaseDialog, type ShowcaseEntry } from './Showcase';

interface AssessCapability { id: string; name: string; risk_level: string; detected: boolean }
interface AssessResponse {
  summary: string;
  risk_score: number;
  blast_radius: string;
  capabilities: AssessCapability[];
  proposed_profile: {
    boundaries: { allowed_actions: string[]; restricted_actions: string[]; max_transaction_limit: number };
    approved: boolean;
  };
}

const EXAMPLE = 'Read supplier invoices, update vendor bank details, and pay approved invoices by wire transfer';


const FEATURES = [
  {
    id: 'boundaries',
    title: 'Boundaries decided before the build',
    body: 'Describe the agent in a sentence and get back what it will be able to reach, how much damage that allows, and a safety profile written for a person to approve.'
  },
  {
    id: 'gate',
    title: 'An approval gate that is not a label',
    body: 'A profile nobody has approved cannot authorize autonomous action. Until a named person accepts the boundaries, every action is held for review — however harmless it looks.'
  },
  {
    id: 'decision',
    title: 'Deterministic enforcement, with the reason',
    body: 'Allow, hold for a human, or block. The same input always reaches the same decision: no model sits in the enforcement path, so a decision can be replayed and argued with.'
  },
  {
    id: 'benchmark',
    title: 'Detection measured, and published with its misses',
    body: '87.0% recall at a 0.0% false-positive rate over a 74-case labelled corpus, against always-allow and always-review baselines. The corpus is self-authored, so treat it as an upper bound. Every miss is published by identifier.'
  },
  {
    id: 'evidence',
    title: 'Evidence you hold',
    body: 'Every decision is recorded with a SHA-256 of what was decided, in your own SQLite file. Open it with any SQLite tool; there is no export to request.'
  },
  {
    id: 'spec',
    title: 'An open specification, not a config file',
    body: 'The profile format is specified in RFC 2119 language with a JSON Schema, 17 conformance vectors authored from the spec text, and a second implementation in another language that agrees on all of them.'
  },
  {
    id: 'frameworks',
    title: 'Mapped to frameworks people already use',
    body: 'Policies carry OWASP Top 10 for LLMs and NIST AI RMF mappings, and detection classes are crosswalked to MITRE CWE — including six named as explicitly out of scope.'
  },
  {
    id: 'mcp',
    title: 'MCP built in',
    body: 'Nine tools over Model Context Protocol, three of which need no credential and never touch a database. Point your coding agent at the hub and it can assess, validate and evaluate directly.'
  }
];

const RADIUS_STYLES: Record<string, string> = {
  low: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
  medium: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
  high: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
  critical: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20'
};

/**
 * The picture that goes with a feature. A screenshot where the answer is a
 * screen, a drawing where it is an idea, the measurement where it is a number.
 */
const FeatureVisual: React.FC<{ id: string; dark: boolean }> = ({ id, dark }) => {
  switch (id) {
    case 'boundaries':
      return <ShotVisual src="boundaries" alt="The allowed and blocked action lists in a profile" dark={dark} />;
    case 'gate':
      return <GateVisual />;
    case 'decision':
      // Past the verdict banner, onto the flow and the reasons.
      return <ShotVisual src="decision-block" alt="A blocked decision, with the rules that produced it" dark={dark} shift={-4} />;
    case 'benchmark':
      return <BenchmarkVisual dark={dark} />;
    case 'evidence':
      return <ShotVisual src="traces" alt="Execution traces with an evidence timeline" dark={dark} />;
    case 'spec':
      return <SpecVisual />;
    case 'frameworks':
      return <FrameworkVisual />;
    case 'mcp':
      return <McpVisual dark={dark} />;
    default:
      return null;
  }
};

/** The reading column: everything except the animated story, which is wider. */
const Column: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto w-full max-w-4xl space-y-10 xl:max-w-6xl 2xl:max-w-page">{children}</div>
);

export const Home: React.FC<{ setActiveTab: (tab: string) => void }> = ({ setActiveTab }) => {
  // The screenshots exist in both themes; show the one the reader is in.
  const isDark = useIsDark();
  const [description, setDescription] = useState(EXAMPLE);
  const [result, setResult] = useState<AssessResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function assess(event: React.FormEvent) {
    event.preventDefault();
    if (!description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/assess', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description })
      });
      if (!res.ok) throw new Error(`The assessment service returned ${res.status}.`);
      setResult(await res.json() as AssessResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach the assessment service.');
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  const detected = result?.capabilities?.filter((cap) => cap.detected) ?? [];
  const [showcase, setShowcase] = useState<ShowcaseEntry | null>(null);

  // Reading width, not a fixed one. A 900px column in a 2000px window is mostly
  // empty margin; a 1400px paragraph is unreadable. So the reading column grows
  // with the viewport and the paragraphs inside it stay capped — while the
  // animated story, which is a picture rather than prose, takes the full width
  // of the shell.
  return (
    <div className="space-y-10 py-10">

     <Column>
      <header className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h1 className="mx-auto max-w-4xl text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Decide what your AI agent is allowed to do — before you build it.
        </h1>
        <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
          Describe the agent you are about to build. AgenticScale works out what it will be able to reach,
          how much damage it could do, and writes a safety profile for a person to approve. Then it enforces
          that profile at runtime, on your own machine.
        </p>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          No account · No API key · Run locally for private work
        </p>
      </header>

     </Column>

      <ScrollStory />

     <Column>
      <Reveal>
      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Try it — right here, nothing to install
        </h2>
        <form onSubmit={assess} className="mt-4 space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300" id="assessment-privacy">
            This form sends your description to the AgenticScale server. On the online demo, use sample details;
            download the hub to keep assessments on your own machine.
          </p>
          <label htmlFor="agent-description" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            What will your agent do?
          </label>
          <textarea
            id="agent-description"
            aria-describedby="assessment-privacy"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {busy ? 'Assessing…' : 'Assess this agent'}
          </button>
        </form>

        {error && (
          <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-6 space-y-4 border-t border-slate-900/5 pt-6 dark:border-white/10">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ring-1 ring-inset ${RADIUS_STYLES[result.blast_radius] ?? RADIUS_STYLES.medium}`}>
                {result.blast_radius} blast radius
              </span>
              <span className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                Risk score {result.risk_score}/100
              </span>
            </div>

            {detected.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  What this agent will be able to reach
                </p>
                <ul className="mt-2 space-y-1.5">
                  {detected.map((cap) => (
                    <li key={cap.id} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-brand-500" />
                      {cap.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Proposed: allowed</p>
                <p className="mt-1 font-mono text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  {result.proposed_profile.boundaries.allowed_actions.join(', ')}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Proposed: never allowed</p>
                <p className="mt-1 font-mono text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  {result.proposed_profile.boundaries.restricted_actions.slice(0, 4).join(', ')}
                </p>
              </div>
            </div>

            <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
              <strong className="font-bold">This profile is unapproved, and that is enforced.</strong> Until a
              person reads these boundaries and accepts them by name, every action the agent takes is held for
              review — however harmless it looks. This assessment was not saved to the application database.
            </p>
          </div>
        )}
      </section>
      </Reveal>

      <Reveal>
      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <TerminalSquare className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Then run the whole platform on your own machine
          </h2>
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          This site is a demo of software you are meant to run yourself. Download one archive and you have
          the same dashboard, API, policy engine and MCP server you are looking at now — storing its data in a
          SQLite file you own, bound to your loopback address, with no account and no sign-in.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-2xl bg-slate-900 p-4 text-xs leading-relaxed text-slate-100 dark:bg-slate-950">
{`curl -LO https://agenticscale.org/download/agenticscale-hub.tgz
tar xzf agenticscale-hub*.tgz
cd agenticscale-hub-* && ./agenticscale`}
        </pre>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
          Open <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-800">http://127.0.0.1:7777</code>.
          Needs Node.js 22.5 or newer and nothing else — no install step, no dependencies to audit.
        </p>
        <button
          type="button"
          onClick={() => setActiveTab('download')}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-300"
        >
          Exact filename, checksum and options <ArrowRight className="h-3 w-3" />
        </button>
      </section>
      </Reveal>

      <Reveal>
      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          What you get
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          The screens below are the real ones, and the numbers are the ones the benchmark produced.
        </p>
        <div className="mt-5 grid gap-x-6 gap-y-7 sm:grid-cols-2 xl:grid-cols-3">
          {FEATURES.map((feature) => (
            <div key={feature.title}>
              <FeatureVisual id={feature.id} dark={isDark} />
              <p className="mt-3 text-sm font-bold text-slate-900 dark:text-white">{feature.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{feature.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 max-w-4xl rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600 dark:bg-white/5 dark:text-slate-300">
          Every figure above is measured. The claims register names what backs each one, and lists what we
          cannot prove yet.
        </p>
      </section>
      </Reveal>

      <Reveal>
      <section className="glass-panel rounded-3xl p-6 sm:p-8">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          The actual interface
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          Not mock-ups: these are screenshots of a running instance with the sample fleet loaded, the same
          one the download starts with. Open one for a closer look, or go straight to it.
        </p>
        <div className="mt-6 grid gap-5 sm:grid-cols-3">
          {SHOWCASE.map((shot) => (
            <button
              key={shot.tab}
              type="button"
              // A closer look first. Navigating straight in is a large step to
              // take on the strength of a thumbnail, and the page you land on
              // assumes you already know what it is.
              onClick={() => setShowcase(shot)}
              aria-haspopup="dialog"
              // `flex flex-col` rather than the default: a grid stretches every
              // cell to the row's height, and a browser centres a button's content
              // vertically inside a button taller than its content. The card whose
              // title wrapped to one line instead of two therefore sat 10px lower
              // than its neighbours. Flex lays the children out from the top.
              className="group flex flex-col text-left"
            >
              <Tilt className="block overflow-hidden rounded-xl border border-slate-900/10 bg-slate-100 shadow-sm transition group-hover:ring-2 group-hover:ring-brand-500/40 dark:border-white/10 dark:bg-slate-950">
                <img
                  src={`/media/${isDark ? 'dark' : 'light'}/${shot.src}.webp`}
                  alt={shot.alt}
                  loading="lazy"
                  decoding="async"
                  className="block h-40 w-full object-cover object-top xl:h-48"
                />
              </Tilt>
              <span className="mt-3 block text-sm font-bold text-slate-900 dark:text-white">{shot.title}</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-600 dark:text-slate-300">{shot.body}</span>
            </button>
          ))}
        </div>
      </section>
      </Reveal>

      <section className="grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setActiveTab('verify')}
          className="glass-panel group flex flex-col items-start rounded-3xl p-6 text-left transition hover:ring-2 hover:ring-brand-500/40"
        >
          <ScrollText className="h-5 w-5 text-brand-600 dark:text-brand-300" />
          <p className="mt-3 text-base font-bold text-slate-900 dark:text-white">Verify every claim</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            Every capability this project asserts, the artifact behind it, and — with equal prominence —
            everything it cannot yet prove.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-600 dark:text-brand-300">
            Open the register <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('simulate')}
          className="glass-panel group flex flex-col items-start rounded-3xl p-6 text-left transition hover:ring-2 hover:ring-brand-500/40"
        >
          <ShieldCheck className="h-5 w-5 text-brand-600 dark:text-brand-300" />
          <p className="mt-3 text-base font-bold text-slate-900 dark:text-white">See enforcement working</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            Send an agent action through the live gateway and watch it be allowed, held for a human, or
            blocked outright — with the reason.
          </p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-600 dark:text-brand-300">
            Open the simulator <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </span>
        </button>
      </section>
     </Column>

      <ShowcaseDialog
        entry={showcase}
        dark={isDark}
        onClose={() => setShowcase(null)}
        onOpenTab={setActiveTab}
      />
    </div>
  );
};
